import { derive, relativeHumidityAt } from './psychrometrics.js'
import type { AppState, Reason, Recommendation, Urgency, Verdict } from './types.js'

/** Ab diesem Delta gilt der Trocknungseffekt als deutlich (g/m³). */
const DELTA_STRONG = 1.5
/** Messunsicherheit beider Fühler zusammen (g/m³). Darunter ist der Effekt nicht belastbar. */
const DELTA_NOISE = 0.5

/** Obergrenze der als unbedenklich geltenden Raumluftfeuchte (Umweltbundesamt: 40–60 %). */
const RH_TOO_HUMID = 65
/** Ab hier ist die Raumluft eher zu trocken. */
const RH_DRY = 35
/** Ab hier sollte nur noch der Luftqualität wegen gelüftet werden. */
const RH_VERY_DRY = 30

const VERDICT_ORDER: Verdict[] = ['no', 'neutral', 'weak', 'yes']

function capVerdict(verdict: Verdict, max: Verdict): Verdict {
  return VERDICT_ORDER.indexOf(verdict) > VERDICT_ORDER.indexOf(max) ? max : verdict
}

/** Stoßlüftungsdauer nach Außentemperatur; je kälter, desto schneller der Luftaustausch. */
export function ventilationDuration(outdoorT: number): [number, number] {
  if (outdoorT <= 0) return [3, 5]
  if (outdoorT <= 10) return [5, 10]
  if (outdoorT <= 15) return [10, 15]
  return [15, 25]
}

function baseVerdict(delta: number): Verdict {
  if (delta >= DELTA_STRONG) return 'yes'
  if (delta >= DELTA_NOISE) return 'weak'
  if (delta > -DELTA_NOISE) return 'neutral'
  return 'no'
}

function fmt(value: number, digits = 1): string {
  return value.toFixed(digits).replace('.', ',')
}

/**
 * Bewertet, ob Lüften gerade sinnvoll ist.
 * Regelwerk und Schwellwerte sind in docs/03-entscheidungslogik.md dokumentiert.
 */
export function evaluate(state: AppState): Recommendation {
  const indoor = derive(state.indoor)
  const outdoor = derive(state.outdoor)
  const delta = indoor.absolute - outdoor.absolute
  const projectedIndoorRh = relativeHumidityAt(outdoor.t, outdoor.rh, indoor.t)

  let verdict = baseVerdict(delta)
  let urgency: Urgency = 'normal'
  const reasons: Reason[] = []

  // Hauptbegründung: Vergleich der absoluten Feuchte.
  if (delta >= DELTA_NOISE) {
    reasons.push({
      kind: 'good',
      text: `Die Außenluft enthält ${fmt(delta)} g/m³ weniger Wasser als die Raumluft. Lüften trocknet.`,
    })
  } else if (delta <= -DELTA_NOISE) {
    reasons.push({
      kind: 'warn',
      text: `Die Außenluft enthält ${fmt(-delta)} g/m³ mehr Wasser als die Raumluft. Lüften bringt Feuchtigkeit herein.`,
    })
  } else {
    reasons.push({
      kind: 'info',
      text: 'Innen und außen ist praktisch gleich viel Wasser in der Luft. Zum Entfeuchten bringt Lüften nichts – für frische Luft und CO₂-Abtransport trotzdem kurz stoßlüften.',
    })
  }

  // R1 – Raumluft zu feucht: kleiner Trocknungseffekt wird wichtiger.
  if (indoor.rh > RH_TOO_HUMID) {
    urgency = 'high'
    reasons.push({
      kind: 'warn',
      text: `Die Raumluft liegt mit ${fmt(indoor.rh, 0)} % über dem empfohlenen Bereich von 40–60 %. Schimmelrisiko.`,
    })
    if (verdict === 'weak') verdict = 'yes'
  }

  // R2 – Raumluft bereits trocken.
  if (indoor.rh < RH_VERY_DRY && delta > 0) {
    verdict = capVerdict(verdict, 'no')
    urgency = 'low'
    reasons.push({
      kind: 'info',
      text: `Die Raumluft ist mit ${fmt(indoor.rh, 0)} % schon sehr trocken. Weiter entfeuchten bringt nichts, nur kurz für die Luftqualität lüften.`,
    })
  } else if (indoor.rh < RH_DRY && delta > 0) {
    verdict = capVerdict(verdict, 'weak')
    urgency = 'low'
    reasons.push({
      kind: 'info',
      text: `Die Raumluft ist mit ${fmt(indoor.rh, 0)} % eher trocken. Nur kurz lüften, sonst wird es unangenehm trocken.`,
    })
  }

  // R3 – Kondensationsgefahr an der kältesten Oberfläche.
  const surfaceT = indoor.t - state.surfaceOffsetK
  if (Number.isFinite(outdoor.dewPoint) && outdoor.dewPoint > surfaceT) {
    verdict = 'no'
    urgency = 'high'
    reasons.push({
      kind: 'warn',
      text: `Der Taupunkt der Außenluft liegt bei ${fmt(outdoor.dewPoint)} °C und damit über der angenommenen kältesten Oberfläche im Raum (${fmt(surfaceT)} °C). Die hereingeholte Luft würde dort kondensieren.`,
    })
  }

  // R4 – Energiehinweise, ohne Einfluss auf das Verdikt.
  if (outdoor.t < indoor.t - 10) {
    reasons.push({
      kind: 'info',
      text: 'Großer Temperaturunterschied: kurz und weit öffnen, Heizung währenddessen herunterdrehen.',
    })
  } else if (outdoor.t > indoor.t + 3) {
    reasons.push({
      kind: 'info',
      text: 'Draußen ist es wärmer als drinnen. Lüften bringt Wärme herein – im Sommer besser nachts oder früh morgens.',
    })
  }

  return {
    verdict,
    urgency,
    deltaAbsolute: delta,
    durationMinutes: verdict === 'no' ? null : ventilationDuration(outdoor.t),
    reasons,
    projectedIndoorRh,
    indoor,
    outdoor,
  }
}

export const VERDICT_LABEL: Record<Verdict, string> = {
  yes: 'Ja, lüften!',
  weak: 'Ja – aber wenig Effekt',
  neutral: 'Zum Entfeuchten: nein',
  no: 'Lieber nicht lüften',
}

export const VERDICT_SHORT: Record<Verdict, string> = {
  yes: 'Fenster auf.',
  weak: 'Kannst du machen.',
  neutral: 'Kurz für frische Luft.',
  no: 'Fenster zu lassen.',
}
